import { useEffect, useState } from 'react'
import apiClient from '../api/client'
import styles from './OverviewPage.module.css'

interface Campaign {
  id: number
  product: string
  audience: string
  platform: string
  budget: string
  strategy: string
  performance_score: number
  headline: string
  primary_text: string
  call_to_action: string
  status?: string
}

interface DashboardSummary {
  campaign_count: number
  total_spend: number
  total_impressions: number
  total_clicks: number
  avg_ctr: number
  avg_cpc: number
  avg_roas: number
}

function KpiWidget({ label, value, icon, color }: { label: string; value: string; icon: string; color: string }) {
  return (
    <div className={styles.kpiCard}>
      <span className={styles.kpiIcon} style={{ background: color + '18', color }}>{icon}</span>
      <div className={styles.kpiContent}>
        <div className={styles.kpiValue}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
      </div>
    </div>
  )
}

export function OverviewPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null)

  async function fetchCampaigns() {
    try {
      const [campsRes, summaryRes] = await Promise.all([
        apiClient.get('/campaigns'),
        apiClient.get('/dashboard/summary').catch(() => ({ data: null })),
      ])
      setCampaigns(campsRes.data)
      if (summaryRes.data) setSummary(summaryRes.data)
    } catch (error) {
      console.error('Failed to load campaigns', error)
    }
  }

  useEffect(() => { fetchCampaigns() }, [])

  async function deleteCampaign(id: number) {
    if (!window.confirm('Delete this campaign?')) return
    try {
      await apiClient.delete(`/campaigns/${id}`)
      setCampaigns(prev => prev.filter(c => c.id !== id))
    } catch (error) {
      console.error(error)
      alert('Failed to delete campaign')
    }
  }

  function editCampaign(campaign: Campaign) {
    setEditingCampaign({ ...campaign })
  }

  function updateEditingField(key: keyof Campaign, value: string) {
    if (!editingCampaign) return
    setEditingCampaign({
      ...editingCampaign,
      [key]: key === 'performance_score' ? Number(value) : value,
    })
  }

  async function saveCampaign() {
    if (!editingCampaign) return
    try {
      await apiClient.put(`/campaigns/${editingCampaign.id}`, editingCampaign)
      setCampaigns(prev =>
        prev.map(c => (c.id === editingCampaign.id ? editingCampaign : c))
      )
      setEditingCampaign(null)
    } catch (error) {
      console.error(error)
      alert('Failed to update campaign')
    }
  }

  const editFields = [
    'product', 'audience', 'platform', 'budget', 'strategy',
    'performance_score', 'headline', 'primary_text', 'call_to_action',
  ] as (keyof Campaign)[]

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.heading}>Dashboard</h1>
        <p className={styles.subheading}>Overview of your AI-generated ad campaigns</p>
      </div>

      {/* KPIs */}
      <section className={styles.kpiGrid}>
        <KpiWidget label="Total Campaigns" value={String(campaigns.length)} icon="📁" color="#2563eb" />
        <KpiWidget label="AI Generated" value="100%" icon="🤖" color="#7c3aed" />
        <KpiWidget label="Total Spend" value={summary ? `₹${summary.total_spend.toFixed(2)}` : '—'} icon="💰" color="#059669" />
        <KpiWidget label="Avg ROAS" value={summary ? `${summary.avg_roas.toFixed(2)}x` : '—'} icon="📈" color="#d97706" />
        <KpiWidget label="Total Clicks" value={summary ? summary.total_clicks.toLocaleString() : '—'} icon="👆" color="#0891b2" />
        <KpiWidget label="Avg CTR" value={summary ? `${(summary.avg_ctr * 100).toFixed(2)}%` : '—'} icon="🎯" color="#db2777" />
      </section>

      {/* Edit Form */}
      {editingCampaign && (
        <section className={styles.editSection}>
          <h2 className={styles.sectionTitle}>Edit Campaign</h2>
          <div className={styles.editGrid}>
            {editFields.map(field => (
              <div key={field} className={styles.editField}>
                <label>{String(field).replace(/_/g, ' ')}</label>
                <input
                  className={styles.editInput}
                  value={String(editingCampaign[field] ?? '')}
                  type={field === 'performance_score' ? 'number' : 'text'}
                  onChange={e => updateEditingField(field, e.target.value)}
                />
              </div>
            ))}
          </div>
          <div className={styles.editActions}>
            <button className={styles.btnSave} onClick={saveCampaign}>Save Changes</button>
            <button className={styles.btnCancel} onClick={() => setEditingCampaign(null)}>Cancel</button>
          </div>
        </section>
      )}

      {/* Table */}
      <section className={styles.tableSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Generated Campaigns</h2>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
            {campaigns.length} total
          </span>
        </div>

        {campaigns.length === 0 ? (
          <p className={styles.empty}>No campaigns yet. Go to Create to generate your first campaign.</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Audience</th>
                  <th>Platform</th>
                  <th>Budget</th>
                  <th>Score</th>
                  <th>Headline</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id}>
                    <td className={styles.campaignName}>{c.product}</td>
                    <td>{typeof c.audience === 'object' ? JSON.stringify(c.audience) : c.audience}</td>
                    <td>{c.platform}</td>
                    <td>{c.budget}</td>
                    <td>{c.performance_score}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.headline}
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${c.status === 'active' ? styles.statusActive : styles.statusDraft}`}>
                        {c.status || 'draft'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionCell}>
                        <button className={styles.actionBtn} onClick={() => editCampaign(c)}>Edit</button>
                        <button className={styles.actionBtnDanger} onClick={() => deleteCampaign(c.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
